import React, { useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  HRDataRetentionModule,
  useHRDataRetentionModule,
} from "../../../components/organisms/data-retention/HRDataRetentionModule";
import { Routes } from "../../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Breadcrumbs, Button, ExpandablePanel } from "@truevault/aquilo";
import { RocketLaunchIcon } from "@heroicons/react/24/solid";
import { Cached as CachedIcon, Done as DoneIcon } from "@mui/icons-material";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  DevInstructionsHrNoticeSlug,
} from "../PrivacyNoticeChecklistItems";
import { NetworkRequest } from "../../../../common/models";

type MatchProps = {
  introId: string;
};
export const EditHrRetentionPolicyPage: React.FC<RouteComponentProps<MatchProps>> = () => {
  const orgId = usePrimaryOrganizationId();
  const hrDataRetention = useHRDataRetentionModule();

  const { answers } = useSurvey(orgId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  const finishedHrNoticeInstructions = answers[DevInstructionsHrNoticeSlug] === "true";

  // `isPublishedRequestRunning` tracks if the publish request is running, while `showSavedSuccess` is used to temporarily show a "Saved" message for a few seconds after the request completes
  const isPublishRequestRunning = NetworkRequest.anyRunning(hrDataRetention.publishRequest);
  const [showSavedSuccess, setShowSavedSuccess] = useState(false);

  const isPublished = hrDataRetention.isPublished || hrDataRetention.publishRequest.success;

  return (
    <StandardPageWrapper
      fullWidth
      requests={[
        hrDataRetention.requests.collectionGroupsRequest,
        hrDataRetention.requests.dataMapRequest,
      ]}
      footer={
        finishedHrNoticeInstructions ? undefined : (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done"
                nextUrl={Routes.hrCenter.root}
                nextDisabled={!(hrDataRetention.isFinished && isPublished)}
                // Show a tooltip if all HR notices have been saved but not yet published
                nextDisabledTooltip={
                  hrDataRetention.isFinished && !isPublished
                    ? "Please publish your retention policy before proceeding."
                    : undefined
                }
              />
            }
          />
        )
      }
    >
      <div style={{ maxWidth: "1125px" }}>
        <div className="tw-boundary">
          <Breadcrumbs
            source="HR Center"
            title="HR Retention Policy"
            sourceRoute={Routes.hrCenter.root}
          />
        </div>

        <div className="tw-boundary">
          <div className="flex items-center justify-between">
            <div className="text-neutral-700">
              For previous HR Retention Policy versions{" "}
              <a className="text-blue-600 underline" href={Routes.documents.root}>
                go to Privacy Docs
              </a>
            </div>
            <div className="flex items-center">
              {(isPublishRequestRunning || showSavedSuccess) && (
                <div className="flex items-center mr-4 text-neutral-600 gap-xs">
                  {showSavedSuccess ? <DoneIcon /> : <CachedIcon />}
                  <span>{showSavedSuccess ? "Saved" : "Saving"}</span>
                </div>
              )}
              <Button
                color="primary"
                onClick={async () => {
                  await hrDataRetention.props.publishEmployeeGroupHrDataRetentionPolicies();

                  setShowSavedSuccess(true);
                  setTimeout(() => {
                    setShowSavedSuccess(false);
                  }, 3000);
                }}
                disabled={!hrDataRetention.isFinished || isPublishRequestRunning}
              >
                <span className="flex items-center">
                  <div style={{ marginRight: "8px" }}>
                    <RocketLaunchIcon className="h-6 w-6" />
                  </div>
                  Publish
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: "896px", margin: "72px auto 0" }}>
          <ExpandablePanel
            title="How to start your HR Retention Policy"
            titleFontSize="lg"
            widthClass="w-full"
          >
            <>
              <p className="text-gray-500 text-[14px] mt-6">
                How to start: For each set of data below, describe how long your business stores the
                data. This table will be included in your online Privacy Notice.
              </p>
              <p className="text-gray-500 text-[14px] mt-6">
                For more information, check out:{" "}
                <a
                  className="text-blue-600 underline font-medium"
                  target="_blank"
                  rel="noreferrer"
                  href="https://knowledge.truevault.com/article/196-create-a-data-retention-policy"
                >
                  How to Create an HR Retention Policy
                </a>
              </p>
              <p className="text-primary-600 text-[14px] font-medium mt-6">
                This policy will not automate data deletion of current and past employees and
                applicants - your company will have to enforce this.
              </p>
            </>
          </ExpandablePanel>

          <HRDataRetentionModule {...hrDataRetention.props} />
        </div>
      </div>
    </StandardPageWrapper>
  );
};
